//@ts-strict-ignore
import { ApolloCache, DocumentNode, MutationFunctionOptions, useMutation, useQuery } from '@apollo/client';
//Note: Typical usage is expected to be via useWorkspace().
import { useLocation } from 'react-router';
import ViewsMutation from '../mutations/view/ViewsMutation.graphql';
import { useDispatch, useSelector } from 'react-redux';
import { initializeViews, useAvailableColumns } from '../../utils/ViewHelper';
import ViewsQuery from '../queries/view/ViewsQuery.graphql';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import {
  Direction,
  Exact,
  IViewMutationResult,
  IViewMutationVariables,
  IViewsQueryResult,
  IViewsQueryVariables,
  IViewUpdateInput,
  Maybe,
} from '../schema/p4types';
import { ICustomColumn } from '../../utils/setColumnsFromArray';
import { useEffect, useMemo } from 'react';
import { IReduxStore } from '../../@types/reduxTypes';
import { IAvailableColumn } from '../../components/view/ColumnSelector';

type IViewsOptions<Entity> = {
  viewScreenName: string;
  altUserId?: number;
  columnOverrides: IWorkspaceColumnOverride;
  initialSortColumn: keyof Entity | string;
  initialSortDir?: Direction;
  dashboard?: boolean;
  columns?: ReadonlyArray<string>;
  miniGrid?: boolean;
  hasDynamicColumns?: boolean;
  defaultQuery?: DocumentNode;
  entity: { workspace?: DocumentNode; type: string };
  embeddedView?: boolean;
  additionalVariables?: any;
  customColumns?: Maybe<{ [key: string]: ICustomColumn }>;
  deferViewInitialize?: boolean;
  availableColumns?: Array<any>;
  defaultTotalColumns?: Array<string>;
};

export const useViews = <Entity>({
  viewScreenName: viewScreenNameFromProps,
  altUserId,
  columnOverrides,
  initialSortColumn,
  initialSortDir: initialSortDirection,
  dashboard,
  columns,
  miniGrid,
  hasDynamicColumns,
  defaultQuery,
  entity: { type, workspace },
  embeddedView,
  additionalVariables,
  customColumns,
  deferViewInitialize,
  defaultTotalColumns,
}: IViewsOptions<Entity>): {
  storedViews?: {
    loading: boolean;
    data: Array<any>;
    refreshViews: () => void;
  };
  viewUpdate: (
    options?:
      | MutationFunctionOptions<IViewMutationResult, Exact<{ input: IViewUpdateInput }>, any, ApolloCache<any>>
      | undefined
  ) => Promise<any>;
  viewScreenName: string;
  availableColumns?: Array<IAvailableColumn>;
} => {
  const location = useLocation<{ genericFilter?: string }>();
  const [viewUpdate] = useMutation<IViewMutationResult, IViewMutationVariables>(ViewsMutation);
  const appUser = useSelector((store: IReduxStore) => store.app.user);
  const dispatch = useDispatch();

  const screenName = viewScreenNameFromProps || location.pathname.slice(1);
  const viewScreenName = screenName.endsWith('/') ? screenName.slice(0, -1) : screenName;

  const availableColumns = useAvailableColumns(
    defaultQuery as DocumentNode,
    { type, workspace },
    {
      embeddedView: embeddedView,
      additionalVariables: additionalVariables,
      columnOverrides: columnOverrides,
      customColumns: customColumns,
    }
  );

  const { loading, data, refetch } = useQuery<IViewsQueryResult, IViewsQueryVariables>(ViewsQuery, {
    variables: {
      systemUserId: altUserId || appUser?.id,
      screen: viewScreenName,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !appUser || deferViewInitialize,
  });

  const genericFilter = useMemo(() => {
    const initialFilter = location?.state?.genericFilter;
    return initialFilter ? { genericFilter: initialFilter } : {};
  }, [location?.state?.genericFilter]);

  //Re-initialize views if the view data or available columns change.
  useEffect(() => {
    if (data) {
      const viewList = data.data?.edges?.map((edge: any) => ({ ...edge?.node, ...genericFilter })) ?? [];

      initializeViews(viewList, viewScreenName, {
        columnOverrides,
        initialSortColumn,
        initialSortDir: initialSortDirection,
        dashboard,
        columns,
        miniGrid,
        hasDynamicColumns,
        availableColumns,
        location,
        dispatch,
        defaultTotalColumns,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, availableColumns]);

  const viewList = useMemo(() => {
    return data?.data.edges.map((edge: any) => ({ ...edge.node, ...genericFilter })) || [];
  }, [data?.data.edges, genericFilter]);

  return useMemo(() => {
    if (!appUser) {
      return { viewScreenName, viewUpdate, availableColumns };
    }

    const initialFilter = location?.state?.genericFilter;
    const genericFilter = initialFilter ? { genericFilter: initialFilter } : {};
    const viewList = data?.data?.edges?.map((edge) => ({ ...edge?.node, ...genericFilter })) ?? [];

    return {
      storedViews: {
        loading,
        data: viewList,
        refreshViews() {
          return refetch();
        },
      },
      viewUpdate: viewUpdate,
      viewScreenName,
      availableColumns,
    };
  }, [appUser, availableColumns, loading, refetch, viewList, viewScreenName, viewUpdate]);
};
